import { useState, useId, useEffect, useCallback, type FormEvent } from "react";
import { Sparkles, Loader2, RefreshCw, Users } from "lucide-react";
import type { InviterListItem, InviterType } from "@invitation-system/shared";
import { createInviter, listInviters } from "../api/client";
import { GeneratedUrlCard } from "./GeneratedUrlCard";

/** Form state for type-specific fields */
interface TypeFields {
  type: InviterType;
  // Sponsor fields
  orgName: string;
  logoUrl: string;
  // Speaker fields
  speakerName: string;
  speakerTitle: string;
  talkTitle: string;
  photoUrl: string;
}

const DEFAULT_TYPE_FIELDS: TypeFields = {
  type: "standard",
  orgName: "",
  logoUrl: "",
  speakerName: "",
  speakerTitle: "",
  talkTitle: "",
  photoUrl: "",
};

export function InviterForm() {
  const [displayName, setDisplayName] = useState("");
  const [typeFields, setTypeFields] = useState<TypeFields>(DEFAULT_TYPE_FIELDS);
  const [validationError, setValidationError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [apiError, setApiError] = useState("");
  const [inviters, setInviters] = useState<InviterListItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState("");
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());

  const inputId = useId();
  const errorId = useId();

  const isInputInvalid = validationError.length > 0;

  // Load inviters list on mount
  const fetchInviters = useCallback(async () => {
    setIsLoadingList(true);
    setListError("");
    try {
      const response = await listInviters();
      setInviters(response.inviters);
    } catch {
      setListError("加载邀请人列表失败，请稍后重试。");
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchInviters();
  }, [fetchInviters]);

  function handleTypeChange(newType: InviterType) {
    setTypeFields((prev) => ({ ...prev, type: newType }));
    if (validationError) setValidationError("");
  }

  function handleTypeFieldChange(field: keyof TypeFields, value: string) {
    setTypeFields((prev) => ({ ...prev, [field]: value }));
    if (validationError) setValidationError("");
  }

  function validateForm(): string | null {
    if (displayName.trim().length === 0) {
      return "请输入邀请人姓名";
    }
    if (typeFields.type === "sponsor") {
      if (!typeFields.orgName.trim()) {
        return "请输入赞助商名称";
      }
    }
    if (typeFields.type === "speaker") {
      if (!typeFields.talkTitle.trim()) {
        return "请输入演讲主题";
      }
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setApiError("");

    const error = validateForm();
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError("");
    setIsCreating(true);

    try {
      // Build request body with type-specific fields
      const body: Record<string, unknown> = {
        displayName: displayName.trim(),
        type: typeFields.type,
      };

      if (typeFields.type === "sponsor") {
        body.orgName = typeFields.orgName.trim();
        if (typeFields.logoUrl.trim()) {
          body.logoUrl = typeFields.logoUrl.trim();
        }
      } else if (typeFields.type === "speaker") {
        body.talkTitle = typeFields.talkTitle.trim();
        if (typeFields.speakerName.trim()) {
          body.speakerName = typeFields.speakerName.trim();
        }
        if (typeFields.speakerTitle.trim()) {
          body.title = typeFields.speakerTitle.trim();
        }
        if (typeFields.photoUrl.trim()) {
          body.photoUrl = typeFields.photoUrl.trim();
        }
      }

      const response = await createInviter(displayName.trim(), {
        type: typeFields.type,
        ...(typeFields.type === "sponsor" && {
          orgName: typeFields.orgName.trim(),
          logoUrl: typeFields.logoUrl.trim() || undefined,
        }),
        ...(typeFields.type === "speaker" && {
          talkTitle: typeFields.talkTitle.trim(),
          speakerName: typeFields.speakerName.trim() || undefined,
          title: typeFields.speakerTitle.trim() || undefined,
          photoUrl: typeFields.photoUrl.trim() || undefined,
        }),
      });

      // Prepend the new inviter to the list, mark as highlighted
      const newItem: InviterListItem = {
        inviterId: response.inviterId,
        displayName: response.displayName,
        url: response.url,
        createdAt: new Date().toISOString(),
        count: 0,
        type: typeFields.type,
        ...(typeFields.type === "sponsor" && {
          orgName: typeFields.orgName.trim(),
          logoUrl: typeFields.logoUrl.trim() || undefined,
          posterStatus: "pending" as const,
        }),
        ...(typeFields.type === "speaker" && {
          talkTitle: typeFields.talkTitle.trim(),
          speakerName: typeFields.speakerName.trim() || undefined,
          title: typeFields.speakerTitle.trim() || undefined,
          photoUrl: typeFields.photoUrl.trim() || undefined,
          posterStatus: "pending" as const,
        }),
      };
      setInviters((prev) => [newItem, ...prev]);
      setHighlightedIds((prev) => new Set(prev).add(response.inviterId));
      setDisplayName("");
      setTypeFields(DEFAULT_TYPE_FIELDS);
    } catch {
      setApiError("生成邀请链接失败，请稍后重试。");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="space-y-8">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">生成邀请链接</h2>
        <p className="text-muted text-sm">
          为每位邀请人生成专属链接，所有通过该链接的报名都会归属于此人。
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate className="bg-surface rounded-2xl shadow-md p-6 space-y-5">
        {/* Type selector */}
        <fieldset>
          <legend className="block text-sm font-medium text-foreground mb-2">
            邀请人类型
          </legend>
          <div className="flex gap-4">
            {([
              { value: "standard", label: "普通" },
              { value: "sponsor", label: "赞助商" },
              { value: "speaker", label: "讲师" },
            ] as const).map(({ value, label }) => (
              <label
                key={value}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  typeFields.type === value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-surface text-muted hover:border-primary-light"
                }`}
              >
                <input
                  type="radio"
                  name="inviter-type"
                  value={value}
                  checked={typeFields.type === value}
                  onChange={() => handleTypeChange(value)}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Display name */}
        <div>
          <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-2">
            邀请人姓名
            <span className="text-error ml-1" aria-hidden="true">*</span>
          </label>
          <input
            id={inputId}
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (validationError) setValidationError("");
            }}
            placeholder="如：张三"
            aria-invalid={isInputInvalid}
            aria-describedby={isInputInvalid ? errorId : undefined}
            className={`w-full rounded-lg border px-4 py-3 text-base text-foreground placeholder:text-muted/60 transition-colors duration-200 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
              isInputInvalid
                ? "border-error ring-1 ring-error/20"
                : "border-border hover:border-primary-light"
            }`}
          />
        </div>

        {/* Sponsor-specific fields */}
        {typeFields.type === "sponsor" && (
          <div className="space-y-4 rounded-lg border border-border bg-background p-4">
            <p className="text-xs font-semibold text-muted uppercase tracking-wide">赞助商信息</p>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                赞助商名称
                <span className="text-error ml-1" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={typeFields.orgName}
                onChange={(e) => handleTypeFieldChange("orgName", e.target.value)}
                placeholder="如：Datawhale"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Logo URL
              </label>
              <input
                type="url"
                value={typeFields.logoUrl}
                onChange={(e) => handleTypeFieldChange("logoUrl", e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              {/* TODO: Replace with file upload when presigned endpoint is available */}
              <p className="mt-1 text-xs text-muted">暂时使用 URL 输入，后续将支持上传</p>
            </div>
          </div>
        )}

        {/* Speaker-specific fields */}
        {typeFields.type === "speaker" && (
          <div className="space-y-4 rounded-lg border border-border bg-background p-4">
            <p className="text-xs font-semibold text-muted uppercase tracking-wide">讲师信息</p>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                演讲主题
                <span className="text-error ml-1" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={typeFields.talkTitle}
                onChange={(e) => handleTypeFieldChange("talkTitle", e.target.value)}
                placeholder="如：从零构建 Serverless 架构"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                讲师姓名
              </label>
              <input
                type="text"
                value={typeFields.speakerName}
                onChange={(e) => handleTypeFieldChange("speakerName", e.target.value)}
                placeholder="如不填则使用邀请人姓名"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                讲师头衔
              </label>
              <input
                type="text"
                value={typeFields.speakerTitle}
                onChange={(e) => handleTypeFieldChange("speakerTitle", e.target.value)}
                placeholder="如：AWS Hero / 首席架构师"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                照片 URL
              </label>
              <input
                type="url"
                value={typeFields.photoUrl}
                onChange={(e) => handleTypeFieldChange("photoUrl", e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="w-full rounded-lg border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted/60 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              {/* TODO: Replace with file upload when presigned endpoint is available */}
              <p className="mt-1 text-xs text-muted">暂时使用 URL 输入，后续将支持上传</p>
            </div>
          </div>
        )}

        {/* Validation error */}
        {isInputInvalid && (
          <p id={errorId} role="alert" className="text-sm text-error">
            {validationError}
          </p>
        )}

        <button
          type="submit"
          disabled={isCreating}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-colors duration-200 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isCreating ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              生成中…
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" aria-hidden="true" />
              生成链接
            </>
          )}
        </button>
      </form>

      {/* API error */}
      {apiError && (
        <div role="alert" className="rounded-lg border border-error bg-error-light px-4 py-3 text-sm text-error">
          {apiError}
        </div>
      )}

      {/* Inviters list */}
      <section aria-label="所有邀请人">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-foreground">
              所有邀请人
            </h3>
            {!isLoadingList && (
              <span className="text-sm text-muted">（共 {inviters.length} 人）</span>
            )}
          </div>
          <button
            type="button"
            onClick={fetchInviters}
            disabled={isLoadingList}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-muted shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="刷新列表"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoadingList ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            刷新
          </button>
        </div>

        {isLoadingList && inviters.length === 0 ? (
          <div className="flex items-center justify-center py-12 bg-surface rounded-xl shadow-sm">
            <Loader2 className="h-6 w-6 text-primary animate-spin" aria-hidden="true" />
            <span className="ml-2 text-muted">正在加载…</span>
          </div>
        ) : listError ? (
          <div role="alert" className="rounded-lg bg-error-light p-4 flex items-center justify-between">
            <p className="text-sm text-error">{listError}</p>
            <button
              type="button"
              onClick={fetchInviters}
              className="cursor-pointer text-sm font-medium text-error underline hover:no-underline"
            >
              重试
            </button>
          </div>
        ) : inviters.length === 0 ? (
          <div className="text-center py-12 bg-surface rounded-xl shadow-sm">
            <p className="text-muted">还没有邀请人，请使用上方表单生成第一个链接。</p>
          </div>
        ) : (
          <div className="space-y-3">
            {inviters.map((item) => (
              <GeneratedUrlCard
                key={item.inviterId}
                inviterId={item.inviterId}
                url={item.url}
                displayName={item.displayName}
                createdAt={item.createdAt}
                highlighted={highlightedIds.has(item.inviterId)}
                count={item.count}
                maxCount={item.maxCount}
                successQrUrl={item.successQrUrl}
                note={item.note}
                type={item.type}
                posterUrl={item.posterUrl}
                posterStatus={item.posterStatus}
                onLimitChanged={fetchInviters}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
